<script setup lang="ts">
import { useUserStore } from "~/stores/user"
import { storeToRefs } from "pinia"
import { NotificationAlertProps } from "../NotificationAlert.props"

const { t } = useI18n()
const { openModal, closeModal } = useModal()
const { addNotification } = useNotification()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const isLogged = useIsLogged()
const { refreshDiscountCodes } = useDiscountCodes()

// Stato di iscrizione alla newsletter dell'utente
const isSubscribedToNewsletter = computed(() => user.value?.isSubscribed)

// CASO A: Utente Anonimo (Guest)
const isGuest = computed(() => !isLogged.value)

// CASO B: Utente Loggato MA Non Iscritto alla Newsletter
const isLoggedNotSubscribed = computed(
  () => isLogged.value && isSubscribedToNewsletter.value === false
)

// CASO C: Utente Loggato E Iscritto alla Newsletter -> Banner nascosto
const shouldShowBanner = computed(
  () => isGuest.value || isLoggedNotSubscribed.value
)

// Testo del banner in base allo stato
const bannerText = computed(() => {
  if (isGuest.value) {
    return t("banner.promoNewsletter.guestText")
  }
  if (isLoggedNotSubscribed.value) {
    return t("banner.promoNewsletter.loggedNotSubscribedText")
  }
  return ""
})

// Testo della CTA in base allo stato
const ctaText = computed(() => {
  if (isGuest.value) {
    return t("banner.promoNewsletter.guestCtaText")
  }
  if (isLoggedNotSubscribed.value) {
    return t("banner.promoNewsletter.loggedNotSubscribedCtaText")
  }
  return ""
})

// Modal login/signup
const LOGIN_MODAL_ID = "promo-newsletter-login-signup-modal"
const NEWSLETTER_MODAL_ID = "promo-newsletter-modal"

const handleOpenLoginModal = () => {
  openModal(LOGIN_MODAL_ID)
}

const handleCloseLoginModal = () => {
  closeModal(LOGIN_MODAL_ID)
}

const handleUserLoggedIn = () => {
  handleCloseLoginModal()
  window.location.reload()
}

// Gestione iscrizione newsletter
const handleNewsletterSubscription = async () => {
  closeModal(NEWSLETTER_MODAL_ID)

  const notificationSuccess: NotificationAlertProps = {
    id: Math.random(),
    type: "success",
    notificationAlertText: t("banner.promoNewsletter.subscriptionSuccess"),
    isWithCloseIcon: true
  }
  addNotification(notificationSuccess)

  // Aggiorna i dati utente e i codici sconto
  setTimeout(async () => {
    const updatedDataUser = {
      ...user.value,
      metadata: {
        ...user.value,
        isSubscribed: true
      }
    }
    await userStore.updateUserData(updatedDataUser)
    await refreshDiscountCodes()
  }, 2000)
}

const handleCloseNewsletterModal = () => {
  closeModal(NEWSLETTER_MODAL_ID)
}

// Gestione azione CTA
const handleCtaClick = () => {
  if (isGuest.value) {
    // CASO A: Apre il popup di registrazione
    handleOpenLoginModal()
  } else if (isLoggedNotSubscribed.value) {
    // CASO B: Apre il modale newsletter
    openModal(NEWSLETTER_MODAL_ID)
  }
}
</script>

<template>
  <section
    v-if="shouldShowBanner"
    class="
      banner-promo-newsletter
      relative
      mb-4
      flex
      min-h-[213px]
      w-full
      items-center
      justify-center
      bg-[url('~/assets/images/bg-banner-promo-newsletter.svg')]
      bg-cover
      bg-center
      bg-no-repeat
    "
  >
    <div class="-z-5 flex w-[276px] flex-col items-center">
      <div class="mb-4 flex items-center gap-3 text-center">
        <UtilsMarkdown class="elephant-bold" :content="bannerText" />
      </div>
      <UtilsButton
        class="beaver-medium flex-shrink-0"
        theme="contained-white"
        order="last"
        :text="ctaText"
        @click="handleCtaClick"
      >
        <template #icon>
          <UtilsIcon name="ArrowRight.svg" class="h-5 w-5" />
        </template>
      </UtilsButton>
    </div>
  </section>

  <!-- Modale per login/signup -->
  <DialogsAndModalsUtilsModal
    :id="LOGIN_MODAL_ID"
    :enable-on-click-outside="true"
    fullscreen
  >
    <DialogsAndModalsStandard
      :title="$t('loginSignup.checkUserPage.headTitle')"
      @on-close="handleCloseLoginModal"
      class="bg-white mx-auto h-[100vh] w-[100vw] md:h-auto md:max-w-[420px]"
    >
      <template #content>
        <PagesLoginOrSignupModalWrapper
          @user-logged-in="handleUserLoggedIn"
          @close="handleCloseLoginModal"
        />
      </template>
    </DialogsAndModalsStandard>
  </DialogsAndModalsUtilsModal>

  <!-- Modale per newsletter -->
  <DialogsAndModalsUtilsModal
    :id="NEWSLETTER_MODAL_ID"
    :enable-on-click-outside="true"
  >
    <DialogsAndModalsNewsletter
      @on-subscription="handleNewsletterSubscription"
      @on-close="handleCloseNewsletterModal"
    />
  </DialogsAndModalsUtilsModal>
</template>

<style scoped lang="scss">
.banner-promo-newsletter :deep(strong) {
  @apply text-red-main;
}
</style>
